import React, { useState } from "react";
import { KpiIcon } from "../../KpiIcons";
import AccordionPanel from "./AccordionPanel";
import AuditTrailModal from "./AuditTrailModal";
import { fmt } from "./utils";

/* ── tiny status pill ── */
const StatusPill = ({ status }) => {
  const s = (status || "pending").toLowerCase();
  const map = {
    approved: { bg: "var(--green2)", color: "var(--green)", label: "Accepted" },
    rejected: { bg: "var(--red2)",   color: "var(--red)",   label: "Rejected" },
    draft:    { bg: "var(--bdr2)",   color: "var(--ink3)",  label: "Draft"    },
    pending:  { bg: "var(--amber2)", color: "var(--amber)", label: "Pending"  },
  };
  const { bg, color, label } = map[s] || map.pending;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 9px", borderRadius: 20, fontSize: 11,
      fontWeight: 700, background: bg, color,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: color, display: "inline-block" }} />
      {label}
    </span>
  );
};

/* ── document icon ── */
const DocBadge = ({ doc }) => {
  if (!doc) return <span style={{ fontSize: 11, color: "var(--ink4)" }}>—</span>;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 5,
      background: "var(--blue3)", color: "var(--blue)",
      fontSize: 11, fontWeight: 600, cursor: "pointer",
    }}>
      <KpiIcon name="audit" size={10} color="var(--blue)" /> doc
    </span>
  );
};

const HistoryRow = ({
  answer,
  idx,
  locs,
  questionId,
  financialYearId,
  isEven,
  onSave,
  canEdit,
  onChat,
  isChatOpen,
  unitSetting,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  /* ── location name — robust string/number comparison ── */
  const locName = (() => {
    const lid = answer.location_id ?? answer.locationId;
    if (!lid) return "—";
    const found = locs.find((l) => String(l.id) === String(lid));
    return found?.name || found?.unitCode || String(lid);
  })();

  const fromDate = (answer.from_date || answer.fromDate || "")?.slice(0, 10);
  const toDate   = (answer.to_date   || answer.toDate   || "")?.slice(0, 10);

  const answeredBy = answer.createdBy
    ? `${answer.createdBy.first_name || ""} ${answer.createdBy.last_name || ""}`.trim() || answer.createdBy.email
    : "—";

  const answeredDate = answer.updatedAt || answer.createdAt
    ? new Date(answer.updatedAt || answer.createdAt).toLocaleDateString()
    : "—";

  const displayValue = `${answer.value ?? "—"} ${answer.unit || ""}`.trim();

  return (
    <>
      {/* ── Summary row ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "13px 0",
          borderBottom: expanded ? "none" : "1px solid var(--bdr2)",
          background: isChatOpen ? "var(--blue3)" : isEven ? "var(--card)" : "#fff",
          cursor: "pointer",
          transition: "background .15s",
        }}
        onClick={() => setExpanded((p) => !p)}
      >
        {/* Expand chevron */}
        <div style={{ width: 52, display: "flex", justifyContent: "center", flexShrink: 0 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 22, height: 22, borderRadius: 6,
            background: expanded ? "var(--blue2)" : "var(--bdr2)",
            transition: "all .15s",
          }}>
            <KpiIcon
              name="chevron"
              size={10}
              color={expanded ? "var(--blue)" : "var(--ink3)"}
              style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform .15s" }}
            />
          </span>
        </div>

        {/* Date range */}
        <div style={{ flex: 1, padding: "0 10px", minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink)" }}>
            {fromDate && toDate ? `${fmt(fromDate)} → ${fmt(toDate)}` : fromDate || "—"}
          </div>
        </div>

        {/* Location */}
        <div style={{ width: 130, flexShrink: 0, padding: "0 10px", fontSize: 12, color: "var(--ink2)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {locName}
        </div>

        {/* Value */}
        <div style={{ width: 120, flexShrink: 0, padding: "0 10px" }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink)" }}>{displayValue}</div>
        </div>

        {/* Document */}
        <div style={{ width: 90, flexShrink: 0, padding: "0 10px" }}>
          <DocBadge doc={answer.document} />
        </div>

        {/* Answered by */}
        <div style={{ width: 140, flexShrink: 0, padding: "0 10px", fontSize: 11.5, color: "var(--ink3)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {answeredBy}
        </div>

        {/* Answer date */}
        <div style={{ width: 110, flexShrink: 0, padding: "0 10px", fontSize: 11.5, color: "var(--ink3)" }}>
          {answeredDate}
        </div>

        {/* Status */}
        <div style={{ width: 105, flexShrink: 0, padding: "0 10px", textAlign: "center" }}>
          <StatusPill status={answer.status} />
        </div>

        {/* Chat button */}
        <div
          style={{ width: 60, flexShrink: 0, padding: "0 10px", textAlign: "center" }}
          onClick={(e) => { e.stopPropagation(); onChat?.(answer); }}
        >
          <button
            className="ahp-btn ahp-btn-g ahp-btn-sm"
            style={{
              padding: "3px 8px",
              background: isChatOpen ? "var(--blue2)" : undefined,
              color:      isChatOpen ? "var(--blue)"  : undefined,
            }}
            title="Open chat for this answer"
          >
            <KpiIcon name="chat" size={12} color={isChatOpen ? "var(--blue)" : "var(--ink3)"} />
          </button>
        </div>

        {/* Audit Trail button */}
        <div
          style={{ width: 60, flexShrink: 0, padding: "0 10px", textAlign: "center" }}
          onClick={(e) => { e.stopPropagation(); setShowAudit(true); }}
        >
          <button
            className="ahp-btn ahp-btn-g ahp-btn-sm"
            style={{ padding: "3px 8px" }}
            title="View audit trail"
          >
            <KpiIcon name="audit" size={12} color="var(--ink3)" />
          </button>
        </div>

        {/* Right spacer */}
        <div style={{ width: 44, flexShrink: 0 }} />
      </div>

      {/* ── Expanded accordion panel ── */}
      {expanded && (
        <AccordionPanel
          answer={answer}
          locs={locs}
          questionId={questionId}
          financialYearId={financialYearId}
          onSave={() => { onSave?.(); }}
          onCancel={() => setExpanded(false)}
          canEdit={canEdit}
          onChat={onChat}
          unitSetting={unitSetting}
          onOpenAuditTrail={() => setShowAudit(true)}
        />
      )}

      {/* ── Audit Trail Modal ── */}
      <AuditTrailModal
        show={showAudit}
        onHide={() => setShowAudit(false)}
        answer={answer}
        questionId={questionId}
        financialYearId={financialYearId ?? answer.financial_year_id}
        locs={locs}
      />
    </>
  );
};

export default HistoryRow;